// ── ARC-TEXT DERIVATION (pure) ───────────────────────────────────────────────────────────────────
// Lifted out of ui/state.js UNCHANGED on 2026-07-29. Nothing here touches state, the DOM or a
// profile — it is string logic — and it now has TWO callers on opposite sides of an import cycle:
// `ui/state.js` (which imports layouts.js) and `layouts.js` itself, which needs the same split when
// it restores a remembered arch line into a two-arc layout. Importing state.js from layouts.js would
// have closed that cycle; a shared leaf module cannot. `ui/state.js` re-exports these so existing
// importers are unaffected.

/**
 * B2 helper — split a long, multi-word org name into two balanced halves for the
 * top/bottom arcs. Returns { top, bottom } or null when the name should stay a
 * single twin-arch (one word, or short enough to arch whole without shrinking).
 * The split point is the word boundary that most evenly balances character length.
 */
export const SPLIT_NAME_MIN_LENGTH = 12; // ≤ this many chars → twin-arch, don't split

/** Split a multi-word string at the word boundary that best balances the two halves'
 *  character length. Returns { top, bottom } or null for a single word. No length
 *  guard — callers decide when a split is warranted. */
export function balanceSplit(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < words.length - 1; i++) {
    const top = words.slice(0, i + 1).join(' ');
    const bottom = words.slice(i + 1).join(' ');
    const diff = Math.abs(top.length - bottom.length);
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  }
  return { top: words.slice(0, bestIdx + 1).join(' '), bottom: words.slice(bestIdx + 1).join(' ') };
}

/**
 * The n-way generalisation of `balanceSplit` — for the GRIP PLATE, whose face is broken into THREE
 * gaps by its hand cutouts (2026-07-30). Returns an array of exactly `n` strings, or null when the
 * name doesn't have `n` words to give.
 *
 * ⚠ `n === 2` DELEGATES TO `balanceSplit`, deliberately, rather than falling out of the general
 * case. The general case minimises `max − min` across the groups, which for two groups is provably
 * the same quantity as `|top.length − bottom.length|` — but "provably the same" is exactly the kind
 * of claim that stops being true when someone changes the cost function here, and every bumper plate
 * in the app rides on the two-way answer. One function, one behaviour, no re-derivation.
 *
 * Ties break toward the EARLIEST cut, matching `balanceSplit`'s strict `<`: the enumeration walks cut
 * positions in ascending order and only a strictly better spread displaces the incumbent.
 *
 * Brute force over all `n−1` cut positions is fine here and always will be: `NAME_MAX_LENGTH` is 40
 * characters, so a realistic name is under a dozen words and the search is a few dozen combinations.
 */
export function balanceSplitN(name, n) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const k = Math.floor(n);
  if (!(k >= 2) || words.length < k) return null;
  if (k === 2) {
    const pair = balanceSplit(words.join(' '));
    return pair ? [pair.top, pair.bottom] : null;
  }
  let best = null;
  let bestSpread = Infinity;
  const cuts = new Array(k - 1);
  const walk = (depth, start) => {
    if (depth === k - 1) {
      const bounds = [0, ...cuts, words.length];
      const parts = [];
      let lo = Infinity;
      let hi = -Infinity;
      for (let g = 0; g < k; g++) {
        const part = words.slice(bounds[g], bounds[g + 1]).join(' ');
        parts.push(part);
        if (part.length < lo) lo = part.length;
        if (part.length > hi) hi = part.length;
      }
      if (hi - lo < bestSpread) { bestSpread = hi - lo; best = parts; }
      return;
    }
    // Every group still to come needs at least one word of its own, which is what caps this cut.
    for (let i = start; i <= words.length - (k - depth - 1); i++) { cuts[depth] = i; walk(depth + 1, i + 1); }
  };
  walk(0, 1);
  return best;
}

/** Balance-split only when the name is long enough to warrant it (else twin-arch a
 *  short/one-word name). */
export function splitNameAcrossArcs(name) {
  const nm = String(name || '').trim();
  if (nm.length <= SPLIT_NAME_MIN_LENGTH) return null;
  return balanceSplit(nm);
}

// B2 — school-name intelligence. A trailing school-type suffix ("High School",
// "HS", "H S", "Jr HS", "Junior High", "Middle School", "Elementary"…) is detected
// + EXPANDED to a canonical, print-worthy form. Ordered most-specific first so
// "Jr HS" matches junior-high before the plain-HS pattern. Each entry strips the
// trailing suffix (only when a place name precedes it) and returns the canonical.
const SCHOOL_SUFFIX_PATTERNS = [
  [/[\s,]+(?:jr\.?|junior)\.?[\s.]*(?:high(?:\s*school)?|h\.?\s*s\.?|hs)\.?$/i, 'Junior High School'],
  [/[\s,]+jhs\.?$/i, 'Junior High School'],
  [/[\s,]+(?:sr\.?|senior)\.?[\s.]*(?:high(?:\s*school)?|h\.?\s*s\.?|hs)\.?$/i, 'Senior High School'],
  [/[\s,]+(?:high\s*school|h\.?\s*s\.?|hs)\.?$/i, 'High School'],
  [/[\s,]+(?:middle\s*school|intermediate(?:\s*school)?|m\.?\s*s\.?|ms)\.?$/i, 'Middle School'],
  [/[\s,]+(?:elementary(?:\s*school)?|primary(?:\s*school)?|e\.?\s*s\.?|es)\.?$/i, 'Elementary School'],
];

/** Split an org name into { place, suffix } where suffix is the canonical expanded
 *  school-type ('High School'…) or null. Only strips when a place name precedes it. */
export function parseSchoolSuffix(name) {
  const s = String(name || '').trim();
  for (const [re, canon] of SCHOOL_SUFFIX_PATTERNS) {
    if (re.test(s)) {
      const place = s.replace(re, '').trim();
      if (place) return { place, suffix: canon };
    }
  }
  return { place: s, suffix: null };
}

/** Two lines read "balanced" when the longer isn't much longer than the shorter —
 *  the cutoff Ryan's examples imply: "Hot Springs"(11)/"Junior High School"(18) is
 *  lopsided; "Northwest Centerville"(21)/"Junior High School"(18) is fine. */
const ARC_BALANCE_RATIO = 1.35;
function balancedPair(a, b) {
  const lo = Math.min(a.length, b.length);
  const hi = Math.max(a.length, b.length);
  return lo > 0 && hi <= lo * ARC_BALANCE_RATIO;
}

/**
 * B2 — derive the top/bottom arc text from the buyer's "Who's this for?" answers,
 * dummy-proofed for school names (Ryan 2026-07-17). Rules:
 *   • name + mascot → clean place name on top (drop "High School"/"HS"/"Jr HS"…),
 *     mascot on the bottom: "Hot Springs High School" + "Trojans" → Hot Springs / Trojans.
 *   • name only, with a school suffix → EXPAND the abbreviation and split by balance:
 *     "Hot Springs HS" → Hot Springs / High School; but a long expansion under a short
 *     place is lopsided, so drop it and split the place: "Hot Springs Jr HS" →
 *     Hot / Springs; while "Northwest Centerville Jr HS" stays Northwest Centerville /
 *     Junior High School.
 *   • name only, no suffix → balance-split a long multi-word name, else twin-arch.
 * The result just seeds the editable Top/Bottom Text fields on Customize Design.
 * @returns {{top:string, bottom:string}}
 */
export function deriveArcText({ name = '', mascot = '' } = {}) {
  const nm = String(name || '').trim();
  const ms = String(mascot || '').trim();
  if (nm && ms) {
    const { place } = parseSchoolSuffix(nm);
    return { top: place || nm, bottom: ms };
  }
  const solo = nm || ms;
  if (!solo) return { top: '', bottom: '' };
  const { place, suffix } = parseSchoolSuffix(solo);
  if (suffix) {
    if (balancedPair(place, suffix)) return { top: place, bottom: suffix };
    // Lopsided (short place under a long expansion) → drop the suffix, split the place
    // (force — even a short "Hot Springs" splits here, since the alternative is worse).
    const sp = balanceSplit(place);
    if (sp) return sp;
    return { top: place, bottom: suffix }; // place unsplittable → keep the expansion
  }
  const sp = splitNameAcrossArcs(solo);
  return sp || { top: solo, bottom: solo };
}

/**
 * The THREE-arc derivation, for the grip plate's three gaps (Ryan 2026-07-30).
 *
 * Its own function rather than a widened `deriveArcText`, because the two differ in the one way that
 * matters: **this one can fail.** `deriveArcText` always returns a pair — a one-word name simply
 * twin-arches, printing itself on both arcs, which is a real design. Twin-arching a name across
 * THREE gaps is not; a plate reading DENVER / DENVER / DENVER is a bug wearing a layout's clothes. So
 * this returns `null` when there aren't three pieces to make, and that null is what the layout picker
 * turns into a locked card ("Needs a three-word name") instead of a plate nobody would order.
 *
 * Where the three pieces come from, in order of preference:
 *   • NAME + MASCOT → the name splits two ways and the mascot takes the third gap.
 *     "Battery Creek High School" + "Panthers" → BATTERY CREEK / HIGH SCHOOL / PANTHERS.
 *   • NAME with a school suffix → `parseSchoolSuffix` peels it off and the PLACE splits two ways.
 *     "Denver North City High School" → DENVER / NORTH CITY / HIGH SCHOOL.
 *   • …and when the place is a single word, the SUFFIX splits instead, which is what makes Ryan's
 *     own second example land: "Denver High School" → DENVER / HIGH / SCHOOL.
 *   • Anything else → a plain three-way balance of the whole string.
 *
 * The suffix-aware routes come first on purpose. A plain `balanceSplitN(name, 3)` happens to give the
 * same answer for the first example, but only by arithmetic accident — it would just as happily cut
 * "HIGH" away from "SCHOOL" on a name where the character counts fell differently. Splitting on what
 * the words MEAN is right for the same reason `deriveArcText` already peels the suffix.
 *
 * @returns {{top:string, left:string, right:string}|null}
 */
/**
 * ONE line of wording, N gaps to fill — the count-generic front door to the two derivations above
 * (2026-08-02).
 *
 * Written because the same question was being answered in four places, each knowing only the counts
 * that existed when it was written: `layouts.js` re-cuts a remembered line, `reseedIdentityIntoPlaceholders`
 * cuts the identity, `applyIdentityText` cuts it again from the Add Logo tab, and `triWordsFor` cuts
 * it three ways. Three of the four understood only `{top,bottom}` and `{top,left,right}` — so the
 * dumbbell's `db-sidelines`, whose two arcs are `left` and `right`, fell through every one of them and
 * showed raw YOUR TEXT / GOES HERE with the buyer's organisation name sitting one tab away.
 *
 * The ladder is deliberately the EXISTING functions rather than a fresh splitter, because they carry
 * rules a generic balance-split does not: `deriveArcText` peels a school suffix and twin-arches a name
 * too short to split, `deriveArcTextTri` refuses rather than printing DENVER / DENVER / DENVER. Only
 * the 4+ case, which no layout reaches today, falls through to the raw n-way balance.
 *
 * @param {string} text  the whole wording, on one line
 * @param {number} n     how many arcs to fill
 * @returns {string[]}   exactly `n` strings (empty array for empty input)
 */
export function distributeAcrossArcs(text, n) {
  const s = String(text || '').trim();
  const k = Math.floor(n);
  if (!s || !(k >= 1)) return [];
  if (k === 1) return [s];
  if (k === 2) {
    const pair = deriveArcText({ name: s });
    return [pair.top || s, pair.bottom || s];
  }
  if (k === 3) {
    const tri = deriveArcTextTri({ name: s });
    // Fewer than three pieces to give → the whole line, repeated. `triWordsFor`'s rule, and Ryan's
    // (2026-07-30): a one- or two-word name fills all three gaps rather than leaving one blank.
    return tri ? [tri.top, tri.left, tri.right] : [s, s, s];
  }
  return balanceSplitN(s, k) || new Array(k).fill(s);
}

export function deriveArcTextTri({ name = '', mascot = '' } = {}) {
  const nm = String(name || '').trim();
  const ms = String(mascot || '').trim();
  if (nm && ms) {
    // A one-word name plus a mascot is only TWO pieces — no third gap to fill, so it fails rather
    // than repeating one of them.
    const pair = balanceSplit(nm);
    return pair ? { top: pair.top, left: pair.bottom, right: ms } : null;
  }
  const solo = nm || ms;
  if (!solo) return null;
  const { place, suffix } = parseSchoolSuffix(solo);
  if (suffix) {
    const p = balanceSplit(place);
    if (p) return { top: p.top, left: p.bottom, right: suffix };
    const s = balanceSplit(suffix);
    if (s) return { top: place, left: s.top, right: s.bottom };
  }
  const tri = balanceSplitN(solo, 3);
  return tri ? { top: tri[0], left: tri[1], right: tri[2] } : null;
}
